import { Button, ButtonLevel, ButtonSize, Checkbox } from '@vizit/atoms';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { AlertCircleIcon } from '@vizit/icons';
import { Dialog } from '@vizit/molecules';
import { plural } from '@vizit/utils';
import * as React from 'react';
import { AffectedCollectionList } from './affected-collection-list';

export interface BenchmarkDeleteDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Called when the dialog opens or closes
   */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * The name of the benchmark being deleted
   */
  benchmarkName: string;
  /**
   * Collections scored against the benchmark. With any, deleting needs an explicit acknowledgement; leave unset
   * while they are unknown to show the plain confirm.
   *
   * @optional
   */
  affectedCollections?: { id: string; name: string }[];
  /**
   * How many collections are scored against the benchmark, as the benchmark itself reports it, so the notice and
   * the acknowledgement need no lookup. `affectedCollections` names the first of them as they load; any beyond
   * the names show as "+N more". Defaults to the number named.
   *
   * @optional
   */
  affectedCollectionCount?: number;
  /**
   * Error from the last delete attempt
   *
   * @optional
   */
  errorMessage?: string | null;
  /**
   * Deletes the benchmark; the dialog shows a loading state until it settles
   */
  onDelete: () => Promise<void>;
}

/**
 * Confirms deleting a benchmark. When collections are scored against it, a red notice counts and lists them and
 * "Delete benchmark" stays disabled until the user ticks that they understand the collections are affected.
 */
export function BenchmarkDeleteDialog(props: BenchmarkDeleteDialogProps) {
  const { isOpen, onOpenChange, benchmarkName, affectedCollections = [], affectedCollectionCount } = props;
  const { errorMessage, onDelete } = props;

  const [isAcknowledged, setIsAcknowledged] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setIsAcknowledged(false);
    }
  }, [isOpen]);

  // Whichever source knows more: a name listed counts even if the benchmark's own count has not caught up.
  const affectedCount = Math.max(affectedCollectionCount ?? 0, affectedCollections.length);
  const collections = plural(affectedCount, 'collection');
  const needsAcknowledgement = affectedCount > 0;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Overlay isDismissable={!isDeleting}>
        <Dialog.Content closeButtonIsDisabled={isDeleting} dialogClassName="gap-8">
          <Dialog.Header>
            <Dialog.Title>Delete this benchmark?</Dialog.Title>
          </Dialog.Header>
          <div className="flex flex-col gap-8 px-12">
            {/* Names can be one long word, so they break anywhere rather than run past the dialog. */}
            <Text
              size={TextSize.Md}
              color={TextColor.Secondary}
              showEllipsis={false}
              className="[overflow-wrap:anywhere]"
            >
              Are you sure you want to delete{' '}
              <Text elementType="span" weight={TextWeight.Semibold} color={TextColor.Primary} showEllipsis={false}>
                {benchmarkName}
              </Text>
              ? This can't be undone.
            </Text>

            {needsAcknowledgement && (
              <>
                <div className="flex flex-col gap-4 border border-error-200 bg-error-50 p-6">
                  <div className="flex items-center gap-3 text-error-700">
                    <AlertCircleIcon size={16} aria-hidden />
                    <Text size={TextSize.Sm} weight={TextWeight.Semibold} className="text-inherit">
                      Affects {collections}
                    </Text>
                  </div>
                  <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
                    {affectedCount === 1 ? 'This collection is' : 'These collections are'} scored against this
                    benchmark. Deleting it removes the benchmark from them, and their scores will no longer be compared
                    against it.
                  </Text>
                  <AffectedCollectionList collections={affectedCollections} count={affectedCount} />
                </div>
                <Checkbox isSelected={isAcknowledged} onChange={setIsAcknowledged} isDisabled={isDeleting}>
                  <Text size={TextSize.Sm} color={TextColor.Primary} showEllipsis={false}>
                    I understand this will affect {collections}
                  </Text>
                </Checkbox>
              </>
            )}

            {errorMessage && (
              <Text role="alert" size={TextSize.Sm} className="text-error-primary" showEllipsis={false}>
                {errorMessage}
              </Text>
            )}
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={() => onOpenChange(false)}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Danger}
              onPress={handleDelete}
              isDisabled={needsAcknowledgement && !isAcknowledged}
              isLoading={isDeleting}
            >
              Delete benchmark
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}
